import React, { useState, useEffect, useRef } from 'react';
import { t } from 'i18next';
import PropTypes from 'prop-types';
import { groupBy, sortBy } from 'lodash';
import { showToast } from 'stores/ToastStore';

import { ACCEPTED_FILE_TYPES } from 'config/accepted-file-types';

import CircleButton from 'components/v2/buttons/CircleButton';
import CheckIcon from 'components/v2/icons/CheckIcon';
import LoadingCircle from 'components/ui/LoadingCircle';
import CargomaticDropzone from 'components/v2/DropZone';

import { useHandleUploadDocuments } from 'utils/DocumentsHooks';
import { sluggify } from 'utils/localization/utils';
import { waitDocumentUpload } from 'utils/DocumentsAPIUtils';
import { getUniqueLatestDocuments, getSignedUrls } from 'utils/DocumentsAPIUtils';
import { getOrderMovementStopDocumentRequirements } from 'helpers/stops';

const getLatestDocument = (docs) => {
  if (!docs.length) {
    return null;
  }
  const sortedDocs = sortBy(docs, 'createdAt');
  return sortedDocs.pop();
};

const generateSignedUrlsLookup = (documents) => {
  return (documents || []).reduce((lookup, document) => {
    const { id, signedUrl } = document;
    return {
      ...lookup,
      [id]: signedUrl,
    };
  }, {});
};

const RequiredStopDocumentsList = (props) => {
  const {
    shipment,
    stop,
    shipperStopDocumentRequirements,
    onFileUploaded,
    onStopDocumentStateUpdate,
  } = props;

  const dropzoneRef = useRef(null);
  const keys = [shipment._id.toString(), stop._id.toString()];

  const [stopDocumentsStateLookup, setStopDocumentsStateLookup] = useState({ requiredDocuments: [], uploadedDocuments: [] });
  const [signedUrlsLookup, setSignedUrlsLookup] = useState({});
  const [currentDocumentsState, setCurrentDocumentsState] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState({});

  useEffect(() => {
    setLoading(true);
    getDocumentsState();
  }, []);

  useEffect(() => {
    getDocumentURLs();
  }, [stopDocumentsStateLookup.uploadedDocuments]);

  useEffect(() => {
    const { requiredDocuments, uploadedDocuments } = stopDocumentsStateLookup;
    setCurrentDocumentsState(getCurrentDocumentsState(requiredDocuments, uploadedDocuments));
  }, [signedUrlsLookup]);

  useEffect(() => {
    onStopDocumentStateUpdate(currentDocumentsState);
  }, [currentDocumentsState]);

  const isFileTypeAlreadyInDatabase = (type) => {
    return stopDocumentsStateLookup.uploadedDocuments.find((f) => f.type === type);
  };

  const getDocumentsState = async () => {
    const requiredDocuments = getOrderMovementStopDocumentRequirements(shipment, stop, shipperStopDocumentRequirements);
    const result = await getUniqueLatestDocuments(keys, requiredDocuments);
    const uploadedDocuments = result?.data?.getUniqueLatestDocuments?.documents || [];
    getDocumentURLs();
    setStopDocumentsStateLookup({ requiredDocuments, uploadedDocuments });
    setCurrentDocumentsState(getCurrentDocumentsState(requiredDocuments, uploadedDocuments));
    setLoading(false);
  };

  const getDocumentURLs = async () => {
    const signedUrlsResult = await getSignedUrls([shipment._id.toString(), stop._id.toString()]);
    const signedUrls = signedUrlsResult?.data.getSignedUrls.signedUrls;
    setSignedUrlsLookup(generateSignedUrlsLookup(signedUrls));
  };

  const getCurrentDocumentsState = (requiredDocuments, uploadedDocuments) => {
    const groupedUploadedDocuments = groupBy(uploadedDocuments, 'type');
    const currentDocumentState = requiredDocuments.reduce((summary, fileType) => {
      let documentState = {
        fileType,
        id: null,
        file: null,
      };

      const latestUpload = getLatestDocument(groupedUploadedDocuments[fileType] || []);
      if (latestUpload) {
        const { id } = latestUpload;
        documentState = {
          ...documentState,
          id,
          file: signedUrlsLookup[id] || null,
        };
      }

      summary = summary.concat(documentState);
      return summary;
    }, []);

    return sortBy(currentDocumentState, (doc) => doc.name);
  };

  const renderNameOrHyperLink = (doc) => {
    const { id, fileType } = doc;
    const needsHyperLink = isFileTypeAlreadyInDatabase(fileType);
    return needsHyperLink ? (
      <td
        data-testid="td-01206a58"
        id={`stop_document_${fileType}_requirement_name`}>
        <a
          data-testid="a-cd0bb20c"
          href={signedUrlsLookup[id] || null}
          target="blank">
          {t(sluggify(fileType))}
        </a>
      </td>
    ) : (
      <td
        data-testid="td-23a4eff9"
        id={`stop_document_${fileType}_requirement_name`}>
        {t(sluggify(fileType))}
      </td>
    );
  };

  const onUpload = (_shipment, _stop, _type, uploaded, _userVisibility) => {
    onFileUploaded(uploaded);
    setStopDocumentsStateLookup(({ requiredDocuments, uploadedDocuments }) => {
      return {
        requiredDocuments,
        uploadedDocuments: uploadedDocuments.concat(...uploaded),
      };
    });
  };

  const onUploadFailed = (_shipment, _stop, _error) => {
    setLoading(false);
  };

  const setDocumentTypeUploading = (docType) => {
    setUploading((currentState) => {
      if (currentState[docType]) {
        return {
          ...currentState,
          [docType]: false,
        };
      }
      return {
        ...currentState,
        [docType]: true
      };
    });
  };

  const { handleUpload } = useHandleUploadDocuments(
    shipment,
    null,
    onUpload,
    onUploadFailed
  );

  const uploadDocument = async (files, docType) => {
    try {
      dropzoneRef?.current?.open();
      let documentResult;
      documentResult = await handleUpload(
        files.map(
          (file) =>
            new File([file], docType, {
              ...file.options,
              type: file.type,
            }),
        ),
        docType,
      );
      if (documentResult?.data) {
        documentResult = await waitDocumentUpload(documentResult.data.key);
      }
      return documentResult;
    } catch (e) {
      console.log('error in uploadDocument: ', e);
    }
  };

  const upload = async (files, documentType) => {
    try {
      if (files.length) {
        const documentResult = await uploadDocument(files, documentType);
        return documentResult;
      }
    } catch (e) {
      if (onUploadFailed) onUploadFailed(e);
      showToast(t('UPLOAD_FAILED'), null, 'error');
      console.log('error in document upload: ', e);
    }
  }

  const renderRowIcon = (document, isUploading, setDocumentUploading) => {
    try {
      if (isUploading) {
        return (
          (<td data-testid="td-d4eef667">
            <LoadingCircle dataTestId="LoadingCircle-f164c145" text={t('PROCESSING_FILES')} />
          </td>)
        );
      }
      if (
        !document.file &&
        !isFileTypeAlreadyInDatabase(document.fileType)
      ) {
        return (
          (<td data-testid="td-a34d4587">
            <CargomaticDropzone
              dataTestId="CargomaticDropzone-45d480b2"
              onDrop={async (files) => {
                setDocumentUploading();
                await upload(files, document.fileType);
                setDocumentUploading();
              }}
              style={{ padding: 0, borderWidth: 0, borderRadius: 0 }}
              multiple={false}
              acceptedFiles={ACCEPTED_FILE_TYPES}
              disabled={isUploading}
              dropzoneMessage=""
              button />
          </td>)
        );
      }
      return (
        (<td data-testid="td-ab6fa964">
          <CircleButton dataTestId="CircleButton-3758aad1" onClick={() => {}} color="green">
            <CheckIcon className="w-5 h-5" />
          </CircleButton>
        </td>)
      );
    } catch (e) {
      console.error(`Error rendering row icon: ${e.message}`);
    }
  };

  const renderRows = () => {
    try {
      if (!currentDocumentsState.length) {
        if (!loading) {
          return (
            (<td data-testid="td-c73c21b3">
              <p data-testid="p-8737e312" className="mt-2">
                {t('NO_DOCUMENTS_REQUIRED_FOR_STOP')}
              </p>
            </td>)
          );
        }
        return (
          (<td data-testid="td-e80b09b7">
            <LoadingCircle dataTestId="LoadingCircle-bab59733" text={t('FETCHING_REQUIREMENTS')} />
          </td>)
        );
      }

      return currentDocumentsState.map((document, index) => {
        const { fileType } = document;
        return (
          (<tr
            data-testid={`tr-9754168f-${index}`}
            className="w-full justify-between"
            id={`stop_document_${fileType}_requirement`}>
            {renderNameOrHyperLink(document)}
            {renderRowIcon(document, uploading[fileType], () => setDocumentTypeUploading(fileType))}
          </tr>)
        );
      });
    } catch (e) {
      console.error(`Error rendering rows: ${e.message}`);
    }
  };

  return (
    (<div data-testid="div-9bc27744" className="flex table-wrapper">
      <table data-testid="table-cbf17c01" className="default-table">
        <thead data-testid="thead-febe8b7f">
          <tr data-testid="tr-bc98f8db">
            <th data-testid="th-4fa7b658">{t('DOCUMENT_TYPE')}</th>
            <th data-testid="th-2b41352c">&nbsp;</th>
          </tr>
        </thead>
        <tbody data-testid="tbody-1d1dad58">
          {renderRows()}
        </tbody>
      </table>
    </div>)
  );
};

export default RequiredStopDocumentsList;
